import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import OTPInputComponent from "../Commons/OtpInput/OtpInput";
import { Button } from "../ui/button";
import ChatbotImg from "../../assets/imgs/ai-chat-logo.svg";
import { useSupabaseAuth } from "../../Context/AuthContext/AuthContext";
import checkMarkGreen from "../../assets/imgs/check-mark-green.svg";
import { lineSpinner } from "ldrs";
lineSpinner.register();

export interface VerififcationOtpProps {
  phone: string;
  onResendCode: () => void;
  localError: string | null;
  setLocalError: (error: string | null) => void;
  token: string;
  setToken: (token: string) => void;
  successMessage?: string | null;
}

export const VerififcationOtp: React.FC<VerififcationOtpProps> = ({
  phone,
  onResendCode,
  localError,
  setLocalError,
  token,
  setToken,
  successMessage,
}) => {
  const { verifyOTPCode, resendOtpCodeLoading } = useSupabaseAuth();
  const numInputs = 6;
  const navigate = useNavigate();

  const isValidToken = (otp: string) => {
    if (!otp) return false;
    if (otp.length < numInputs) return false;
    return true;
  };

  const handleVerifyOtp = async (phone: string, otp: string) => {
    if (!isValidToken(otp)) {
      setLocalError("Please input verification code.");
      return;
    }

    const status = await verifyOTPCode(phone, otp);
    if (status) {
      setLocalError(null);
      navigate("/user-details");
    } else {
      setLocalError("Incorrect verification code, please try again.");
    }
  };

  const handleOTPChange = (otp: string) => {
    setToken(otp);
    setLocalError(null);
  };

  return (
    <div className="w-full flex flex-col items-center mt-36 h-screen text-center overflow-hidden">
      <div className="sm:w-1/2 sm:px-10 w-full flex flex-col items-center min-h-[350px]">
        <div className="flex flex-col items-center space-y-3">
          <img
            src={ChatbotImg}
            alt="ChatbotImg"
            className="w-[44px] h-[34px]"
          />
          <div className="font-bold	 text-white text-[32px] ">
            Enter your code
          </div>
        </div>
        <div>
          <div className="mt-5 w-full flex justify-center">
            <OTPInputComponent
              value={token}
              onChange={handleOTPChange}
              numInputs={numInputs}
              correctOTP={localError}
              onVerify={(otp) => handleVerifyOtp(phone, otp)}
            />
          </div>
          {localError && <p className="text-red-500 mt-4">{localError}</p>}

          <div className="mt-4">
            {!resendOtpCodeLoading ? (
              <Button
                variant={"link"}
                size={"default"}
                onClick={onResendCode}
                className="text-white text-[12px] font-medium"
              >
                Send a new code
              </Button>
            ) : (
              <l-line-spinner
                size="25"
                stroke="2"
                speed="1"
                color="white"
              ></l-line-spinner>
            )}
          </div>

          <AnimatePresence>
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{
                  duration: 0.3,
                  ease: "easeOut",
                }}
                className="flex items-center gap-2 mt-4 justify-center w-full"
              >
                <motion.img
                  src={checkMarkGreen}
                  alt="checkMarkGreen"
                  className="w-4 h-4"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, duration: 0.2, ease: "easeOut" }}
                />
                <motion.p
                  className="text-white font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.2 }}
                >
                  {successMessage}
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
